import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1549046701-6bd11cf71796?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.4 + i * 0.16, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Line = ({ children, i, className }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.15, 1.3]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="zi enzi live in fog and light" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-ink/70" />
      </motion.div>

      {/* top meta bar */}
      <motion.div
        style={{ opacity: fade }}
        className="absolute top-20 left-5 sm:left-8 lg:left-12 right-5 sm:right-8 lg:right-12 flex justify-between font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-bone/50"
      >
        <span data-testid="hero-meta-loc">Bristol · UK</span>
        <span>Est. 2026</span>
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 flex h-full flex-col justify-end px-5 sm:px-8 lg:px-12 pb-20 sm:pb-24">
        <h1 className="font-display font-black uppercase text-bone leading-[0.82] tracking-tighter text-[24vw] sm:text-[22vw] lg:text-[19vw]">
          <Line i={0}>Zi</Line>
          <Line i={1} className="text-acid">Enzi</Line>
        </h1>

        <div className="mt-6 sm:mt-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="md:col-span-6 font-mono text-sm sm:text-base leading-relaxed text-bone/70 max-w-md"
            data-testid="hero-tagline"
          >
            Low-end weight from the Bristol underground. Dark trance, hollow dub
            and sub-bass built for concrete rooms and broken hours.
          </motion.p>
          <motion.a
            href="#sound"
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35 }}
            className="md:col-span-6 md:justify-self-end group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-bone hover:text-acid transition-colors duration-300"
          >
            <span className="h-px w-10 bg-acid group-hover:w-16 transition-all duration-300" />
            Enter the sound
          </motion.a>
        </div>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/40">
        <span className="animate-pulse">scroll</span>
      </motion.div>
    </section>
  );
};

export default Hero;
